import type { ClassDefinition } from './class-types.js';
export const BEHAVIOR_IDS = ['balanced', 'hold-ground', 'maximize-damage', 'protect-ally', 'keep-distance', 'skirmisher'] as const;
export type BehaviorId = typeof BEHAVIOR_IDS[number];
export type TargetingBehavior = 'closest' | 'highest-threat';
/** Monster AI packages (`src/content/behaviors/monster-tactics.ts`); a monster names one as its `behavior`. */
export const MONSTER_BEHAVIOR_IDS = ['melee', 'skirmisher'] as const;
export type MonsterBehaviorId = typeof MONSTER_BEHAVIOR_IDS[number];
/** How a monster with a given package fights. Which attacks it has still comes from its stat block. */
export interface MonsterTactics {
  /** The kind of attack it reaches for first when its stat block offers both ("Stab, or Shoot"). */
  favours: 'melee' | 'ranged';
  /** Ranged attacks a melee-first monster spends on heroes out of reach before it commits to melee; after that it shoots only a target it cannot get to. */
  openingShots: number;
  /** Backs away from a hero this close (meters, edge to edge); null stands its ground. */
  keepAway: number | null;
  /** Next target: the nearest, or the nearest one no other monster is fighting in melee. */
  prefer: 'nearest' | 'unengaged';
}
/** How much a hero plays itself; each step adds to the one before. Reactions and player orders work at every step. */
export const AUTONOMY_LEVELS = ['manual', 'attack', 'abilities', 'full'] as const;
export type Autonomy = typeof AUTONOMY_LEVELS[number];
export function autonomyAtLeast(behavior: Pick<BehaviorSettings, 'autonomy'>, level: Autonomy): boolean {
  return AUTONOMY_LEVELS.indexOf(behavior.autonomy) >= AUTONOMY_LEVELS.indexOf(level);
}
export const FURY_GUARDS = ['off', 'big-hits', 'every-hit'] as const;
export type FuryGuard = typeof FURY_GUARDS[number];
export interface BehaviorSettings {
  stance: BehaviorId;
  /** Manual: reactions only. Attack: fights foes in reach, a melee hero also the next foe within a few steps. Abilities: also class
   *  autocasts and revives in reach. Full: the only level that walks on its own: chases any engaged foe, focuses fire with the party,
   *  rescues Dying allies and, while Dying, gets out of reach. Potions follow `healing` alone. */
  autonomy: Autonomy;
  defend: boolean;
  opportunityAttacks: boolean;
  interpose: boolean;
  /** Automatically drink or administer healing potions in combat. */
  healing: boolean;
  /** Autocastable abilities (class powers and Anticipate Danger) the AI must never use by itself. Manual casts still work. */
  autocastOff: string[];
  /** The one attack cantrip that replaces the weapon as this hero's autoattack, or null for the weapon. */
  autoAttack: string | null;
  /** Songweaver's Inspiration: let the AI spend it on allies' rolls. */
  inspiration: boolean;
  /** Use Relentless automatically when damage would drop this hero to 0 HP. Independent of combat AI. */
  relentless: boolean;
  /** Targeting preference when the AI needs to choose an enemy. */
  targeting: TargetingBehavior;
  /** Hunter's Mark: what each attack against the Quarry gains. */
  quarryBonus: 'advantage' | 'damage';
  /** That all you got?!: when a Berserker spends Fury Dice against a hit. Big hits spends a die only when it absorbs all it can, and whatever keeps the hero standing. */
  furyGuard: FuryGuard;
  preferredAttackId: string | null;
  protectedHeroId: string | null;
}
/** Every autocast starts on; the class's first attack cantrip is its autoattack unless the class fights with its weapon first. */
/** A class names its own starting stance (`ClassDefinition.defaultStance`); the AI never branches on a class. */
export function defaultBehavior(_classId?: string, cls?: Pick<ClassDefinition, 'abilities' | 'defaultStance' | 'weaponFirst' | 'interposes'>): BehaviorSettings {
  return { stance: cls?.defaultStance ?? 'balanced', autonomy: 'abilities', defend: true, opportunityAttacks: true, interpose: !!cls?.interposes, healing: true,
    autocastOff: [], autoAttack: cls?.weaponFirst ? null : cls?.abilities.find(ability => ability.autoAttack)?.id ?? null, inspiration: true, relentless: true, targeting: 'closest', quarryBonus: 'advantage', furyGuard: 'big-hits', preferredAttackId: null, protectedHeroId: null };
}
export interface BehaviorStatus { reservedActions: number; decision: string }
/** Whether the AI may use this ability by itself. An attack cantrip is on only while it is the autoattack. */
export function autocasting(behavior: Pick<BehaviorSettings, 'autocastOff' | 'autoAttack'>, ability: { id: string; autoAttack?: boolean }): boolean {
  return ability.autoAttack ? behavior.autoAttack === ability.id : !behavior.autocastOff.includes(ability.id);
}
